import * as React from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
} from "@mui/material";
import { getItemReport } from "../../api/batches";
import { MarkdownView } from "../Chat/Markdown/MarkdownView";
import { usePortalScope } from "@/analytics/PortalScope";

interface Props {
  batchId: string;
  itemId: string | null;
  filename: string;
  onClose: () => void;
}

/** Item reports are markdown (tables, headings, bold flags), rendered with the
 *  same MarkdownView the chat uses so a report reads the same in both places. */
export function ItemReportDialog({ batchId, itemId, filename, onClose }: Props) {
  const [report, setReport] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // Opened from the items table's Report button (items-open_report).
  const paperScope = usePortalScope("report_dialog");

  React.useEffect(() => {
    if (!itemId) return;
    setReport(null);
    setError(null);
    getItemReport(batchId, itemId)
      .then(setReport)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [batchId, itemId]);

  return (
    <Dialog open={!!itemId} onClose={onClose} maxWidth="md" fullWidth slotProps={{ paper: paperScope }}>
      <DialogTitle>Report — {filename}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!error && report === null && <CircularProgress size={20} />}
        {report !== null && (
          <Box data-testid="item-report">
            <MarkdownView source={report} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-ph="close">Close</Button>
      </DialogActions>
    </Dialog>
  );
}
